import {
  FocusEvent,
  ForwardRefExoticComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { IInputProps, Input } from 'core-components/library/inputs'
import { Select, useSource } from 'core-components/library/selects'
import {
  IValidateInputProps,
  ValidationStatus,
  Validator,
} from 'core-components/library/validation'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'

import { NewItemButton } from './components/NewItemButton'
import { needHandleBlur, onBlurHelper } from './helpers/onBlurHelper'
import { useInfinityLoopCheck } from './hooks/useInfinityLoopCheck'
import { useInputProps } from './hooks/useInputProps'
import { useOnInputKeyPress } from './hooks/useOnInputKeyPress'
import { usePopupOpenedState } from './hooks/usePopupOpenedState'
import { IDropdownProps } from './models/IDropdownProps'

interface IDropdownBase<T> extends IDropdownProps<T> {
  prepareInputConfig?: (config: IInputProps) => void
  InputComponent?: ForwardRefExoticComponent<IInputProps>
  ValidateInputComponent?: ForwardRefExoticComponent<IValidateInputProps>
}

export function DropdownBase<T = IForm>({
  dir,
  source,
  caption,
  onChange,
  label,
  placeholder,
  placeholderTemplate,
  value,
  filter,
  checkFn,
  margin,
  disabledInput,
  disabled = false,
  startContent,
  endContent,
  allowCreate,
  createButtonTemplate,
  onCreate,
  maxListPageSize,
  emptyValueLabel,
  classes,
  renderFunction,
  onBlur,
  autoFocus,
  hasHierarchy,
  prepareInputConfig,
  InputComponent = Input,
  hierarchyField,
  onDataLoad,
  valueDependencies,
  itemClickCallback,
  _popupVisible,
  __testId,
  __targetTestId,
}: IDropdownBase<T>) {
  const [inputted, setInputted] = useState<string>('')
  const [inputtedBeforeFocus, setInputtedBeforeFocus] = useState<string>('')
  const [validateStatus, setValidateStatus] = useState<ValidationStatus>(ValidationStatus.Valid)

  const { staticSource, data, wip } = useSource(source, caption, emptyValueLabel)

  const { resetValue, updateValue } = useInfinityLoopCheck<T>(data, source.keyField)
  const { visibilityCallbackHandler, isPopupOpened } = usePopupOpenedState()

  const { setPopupActions, onKeyPress } = useOnInputKeyPress()

  const showCreate = allowCreate && inputted !== ''
  const isAddNewItemButtonVisible =
    showCreate && inputted && !data.find((el) => el[caption] === inputted) && value !== inputted

  const validatorDependencies = useMemo(() => {
    return [disabled, ...(valueDependencies || [])]
  }, [disabled, valueDependencies])

  const setFilter = useCallback(
    (newFilter: IFilter) => {
      if (filter) {
        const mergedFilter: IDataProviderFilter = {
          ...filter,
          filters: [...filter.filters, { ...newFilter }],
        }
        staticSource.setFilter(mergedFilter)
      } else {
        staticSource.setFilter({
          logic: Logic.AND,
          filters: [{ ...newFilter }],
        })
      }
    },
    [filter, staticSource]
  )

  const setNewSourceProperties = useCallback(
    (filterValue: string | number) => {
      const filterByKeyField: IFilter = {
        value: filterValue,
        field: source.keyField,
        operator: Operator.NOTEQUAL,
      }
      setFilter(filterByKeyField)
      if (maxListPageSize) {
        staticSource.setPaging({
          page: 0,
          pageSize: maxListPageSize,
        })
      }
    },
    [maxListPageSize, setFilter, source.keyField, staticSource]
  )

  const handleClick = (item: any) => {
    const isSelectedItemEmpty = emptyValueLabel?.value === item[caption]
    onChange(isSelectedItemEmpty ? '' : item[staticSource.keyField], item)
    setInputted(item[caption])
    onBlur && onBlur()
  }

  const inputValueChanged = (text: string) => {
    setInputted(text)
    const filterByText: IFilter = {
      field: caption,
      operator: Operator.CONTAINS,
      value: text,
    }
    setFilter(filterByText)
  }

  const onChangeHandler = useCallback(
    (text: string) => {
      if (inputted && !text) {
        onChange('')
      }
      inputValueChanged(text)
    },
    [inputted, onChange]
  )

  const onBlurHandler = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (!needHandleBlur(event)) {
        inputRef.current?.focus()
        return
      }

      const changeValueHandler = (newValue: string) => {
        setInputted(newValue)
        setNewSourceProperties(newValue)
      }
      onBlurHelper(event, inputtedBeforeFocus, inputted, changeValueHandler, onBlur)
    },
    [inputtedBeforeFocus, inputted, onBlur, setNewSourceProperties]
  )

  const getInputComponent = () => {
    if (checkFn) {
      return (
        <Validator
          classes={classes?.validatorClasses}
          value={allowCreate ? inputted : value + ''}
          valueDependencies={validatorDependencies}
          checkFn={disabled || !checkFn ? null : checkFn}
          throttle={!!allowCreate}
          onChangeValidation={(status) => {
            if (status !== ValidationStatus.WIP) {
              setValidateStatus(status)
            }
          }}
        >
          <InputComponent
            {...inputProps}
            wrapper={validateStatus === ValidationStatus.Invalid ? 'danger' : 'normal'}
          />
        </Validator>
      )
    }

    return <InputComponent {...inputProps} />
  }

  const inputRef = useRef<HTMLInputElement>(null)

  const inputProps = useInputProps({
    dir,
    startContent,
    endContent,
    classes: classes?.inputClasses,
    disabled,
    disabledInput,
    label,
    placeholder,
    placeholderTemplate,
    autoFocus,
    inputted,
    setInputtedBeforeFocus,
    isPopupOpened,
    inputRef,
    onKeyPress,
    __testId,
    onChange: onChangeHandler,
    onBlur: onBlurHandler,
  })

  if (prepareInputConfig) {
    prepareInputConfig(inputProps)
  }

  useEffect(() => {
    if (filter && !inputted) {
      staticSource.setFilter(filter)
    }
  }, [staticSource, filter, inputted])

  useEffect(() => {
    const setup = () => {
      const handleAllowCreate = () => {
        if (value) {
          updateValue()
          setInputted(`${value}`)
          setNewSourceProperties(value)
        }
      }

      if (!data.length) {
        if (allowCreate) {
          handleAllowCreate()
        }
        return
      }

      if (value) {
        const el = data.find((item) => `${item[source.keyField]}` === `${value}`)

        if (el) {
          updateValue()
          setInputted(`${el[caption]}`)
          setNewSourceProperties(value)
        } else {
          if (allowCreate) {
            handleAllowCreate()
          } else {
            resetValue(value)
            onChange('')
          }
        }
      } else {
        const emptyItem = data.find((el) => emptyValueLabel?.value === el[caption])
        setNewSourceProperties(emptyItem ? emptyItem[caption] : '')
        setInputted(emptyItem ? emptyItem[caption] : '')
        if (emptyItem) {
          updateValue()
        } else {
          resetValue(value)
        }
      }
    }

    setup()
  }, [value, data, resetValue, updateValue])

  return (
    <Select
      wip={wip}
      renderFunction={renderFunction}
      classes={classes?.controlClasses}
      margin={margin}
      source={staticSource}
      caption={caption}
      itemClickCallback={itemClickCallback}
      onClick={handleClick}
      initializePopupRef={setPopupActions}
      disabled={disabled}
      isSelect
      onDataLoad={onDataLoad}
      hasHierarchy={hasHierarchy}
      hierarchyField={hierarchyField}
      visibilityCallback={visibilityCallbackHandler}
      popupHeader={(popupData) => {
        if (isAddNewItemButtonVisible) {
          return (
            <NewItemButton
              createItemTemplate={createButtonTemplate}
              onClick={async () => {
                setInputted('')
                if (onCreate) {
                  onCreate(inputted)
                }
                popupData.action.close()
              }}
            />
          )
        }
        return <></>
      }}
      _popupVisible={_popupVisible}
      __targetTestId={__targetTestId}
    >
      {getInputComponent()}
    </Select>
  )
}
